'use client';

import { createContext, useContext, ReactNode, useState, useCallback } from 'react';
import { PageProps } from 'libs/contentful/queries/page/props';
import { ResolvedVisualSettings } from 'utils/pageHelpers/visualSettings';

interface PageContextType {
  page?: PageProps;
  visualSettings?: ResolvedVisualSettings;
  updateVisualSettings?: (settings: Partial<ResolvedVisualSettings>) => void;
}

const PageContext = createContext<PageContextType>({});

/**
 * Context provider for page-level data.
 * Provides a centralized way to access page-level data within page components.
 *
 * @example
 * <PageProvider page={page} visualSettings={visualSettings}>
 *   <Component />
 * </PageProvider>
 */
export const PageProvider = ({
  children,
  page,
  visualSettings: initialVisualSettings,
}: {
  children: ReactNode;
  page?: PageProps;
  visualSettings?: ResolvedVisualSettings;
}) => {
  const [visualSettings, setVisualSettings] = useState<ResolvedVisualSettings | undefined>(initialVisualSettings);

  const updateVisualSettings = useCallback((settings: Partial<ResolvedVisualSettings>) => {
    setVisualSettings(prev => {
      if (!prev) {
        return undefined;
      }
      return { ...prev, ...settings };
    });
  }, []);

  return (
    <PageContext.Provider value={{ page, visualSettings, updateVisualSettings }}>{children}</PageContext.Provider>
  );
};

/**
 * Hook to access page-level data.
 *
 * @example
 * const Component = () => {
 *   const { page } = usePage();
 *   return <div>{page?.title}</div>;
 * }
 */
export const usePage = () => {
  const context = useContext(PageContext);
  return context;
};
